import {
  Box,
  Container,
  Divider,
  Stack,
  Text,
  Title,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core'
import { PublicLandingHeader } from '../../components/layout/PublicLandingHeader'

function LegalSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Stack gap="sm">
      <Title order={3} fz="lg">
        {title}
      </Title>
      {children}
    </Stack>
  )
}

export function TermsOfService() {
  const theme = useMantineTheme()
  const isDark = useComputedColorScheme('dark') === 'dark'
  const bg = isDark ? theme.colors.dark[7] : '#fff'
  const border = isDark ? theme.colors.dark[4] : 'var(--sat-border-soft)'

  return (
    <Box style={{ background: isDark ? theme.colors.dark[8] : 'var(--sat-bg)', minHeight: '100vh' }}>
      <PublicLandingHeader />
      <Container size="md" py={60}>
        <Box
          p={{ base: 'xl', sm: 48 }}
          style={{ background: bg, border: `1px solid ${border}`, borderRadius: 16 }}
        >
          <Stack gap="lg">
            <Stack gap={4}>
              <Title order={1} fz={{ base: 28, sm: 36 }} c="var(--sat-text)">
                Terms of Service
              </Title>
              <Text fz="sm" c="dimmed">
                FinderAcademy, Inc. &nbsp;·&nbsp; Effective Date: January 1, 2026 &nbsp;·&nbsp; Last Updated: April 1, 2026
              </Text>
            </Stack>

            <Divider />

            <Text c="var(--sat-text-muted)" lh={1.8}>
              These Terms of Service ("Terms") govern your access to and use of the SAT Tutor platform
              provided by FinderAcademy, Inc. ("FinderAcademy", "we", "our", or "us") at
              finderacademy.org (the "Service"). By creating an account or using the Service, you agree
              to be bound by these Terms. If you do not agree, do not use the Service.
            </Text>

            <LegalSection title="1. Eligibility">
              <Text c="var(--sat-text-muted)" lh={1.8}>
                You must be at least 13 years old to use the Service. If you are under 18, you
                represent that a parent or legal guardian has reviewed and agreed to these Terms on
                your behalf. By using the Service, you represent that you have the legal capacity to
                enter into a binding agreement.
              </Text>
            </LegalSection>

            <LegalSection title="2. Accounts">
              <Text c="var(--sat-text-muted)" lh={1.8}>
                You are responsible for maintaining the confidentiality of your account credentials and
                for all activity that occurs under your account. You agree to notify us immediately at
                support@finderacademy.org of any unauthorized use of your account. FinderAcademy will
                not be liable for losses resulting from unauthorized account access due to your failure
                to safeguard your credentials.
              </Text>
            </LegalSection>

            <LegalSection title="3. Subscription and Payments">
              <Text c="var(--sat-text-muted)" lh={1.8}>
                <strong>Plans.</strong> The Service is offered under various subscription plans
                (monthly, annual, or lifetime) as displayed on the pricing page. Prices are in US
                dollars and subject to change with 30 days' notice to existing subscribers.
              </Text>
              <Text c="var(--sat-text-muted)" lh={1.8}>
                <strong>Billing.</strong> Payments are processed securely by Stripe, Inc. By
                subscribing, you authorize FinderAcademy to charge your payment method on a recurring
                basis (for monthly/annual plans) until you cancel.
              </Text>
              <Text c="var(--sat-text-muted)" lh={1.8}>
                <strong>Refunds.</strong> Lifetime plan purchases are final. Monthly and annual
                subscribers may request a pro-rated refund within 7 days of the initial purchase by
                contacting support@finderacademy.org. No refunds are issued after that period.
              </Text>
              <Text c="var(--sat-text-muted)" lh={1.8}>
                <strong>Cancellation.</strong> You may cancel a recurring subscription at any time.
                Cancellation takes effect at the end of the current billing period; you retain access
                until then.
              </Text>
            </LegalSection>

            <LegalSection title="4. Acceptable Use">
              <Text c="var(--sat-text-muted)" lh={1.8}>
                You agree not to:
              </Text>
              <Stack gap={4} pl="md">
                {[
                  'Share your account credentials or allow others to use your subscription;',
                  'Copy, distribute, or republish any Service content without written permission;',
                  'Attempt to reverse-engineer, decompile, or access the Service\'s underlying code;',
                  'Use the Service to transmit spam, malware, or any harmful content;',
                  'Circumvent, disable, or interfere with security features of the Service;',
                  'Use automated tools (bots, scrapers) to extract content from the platform.',
                ].map((item) => (
                  <Text key={item} c="var(--sat-text-muted)" lh={1.8}>
                    · {item}
                  </Text>
                ))}
              </Stack>
              <Text c="var(--sat-text-muted)" lh={1.8}>
                Violation of these rules may result in immediate account suspension or termination
                without refund.
              </Text>
            </LegalSection>

            <LegalSection title="5. Intellectual Property">
              <Text c="var(--sat-text-muted)" lh={1.8}>
                All content on the Service — including but not limited to SAT practice questions,
                course materials, vocabulary sets, UI design, and software — is the exclusive property
                of FinderAcademy or its licensors and is protected by copyright, trademark, and other
                intellectual property laws. You are granted a limited, non-exclusive, non-transferable
                license to access and use the Service for personal, non-commercial educational purposes.
              </Text>
            </LegalSection>

            <LegalSection title="6. Tutor Responsibilities">
              <Text c="var(--sat-text-muted)" lh={1.8}>
                Tutors using the platform to manage students accept responsibility for ensuring their
                students' use of the platform complies with these Terms. Tutors must not add students
                without their informed consent and must handle student data in accordance with
                applicable privacy laws.
              </Text>
            </LegalSection>

            <LegalSection title="7. Disclaimers">
              <Text c="var(--sat-text-muted)" lh={1.8}>
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
                EXPRESS OR IMPLIED. FINDERACADEMY DOES NOT WARRANT THAT THE SERVICE WILL BE
                UNINTERRUPTED, ERROR-FREE, OR THAT ANY PARTICULAR SAT SCORE IMPROVEMENT WILL BE
                ACHIEVED. SAT® IS A REGISTERED TRADEMARK OF THE COLLEGE BOARD, WHICH IS NOT
                AFFILIATED WITH AND DOES NOT ENDORSE FINDERACADEMY.
              </Text>
            </LegalSection>

            <LegalSection title="8. Limitation of Liability">
              <Text c="var(--sat-text-muted)" lh={1.8}>
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, FINDERACADEMY SHALL NOT BE LIABLE
                FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING
                LOSS OF PROFITS, DATA, OR GOODWILL. OUR TOTAL LIABILITY TO YOU FOR ANY CLAIM ARISING
                FROM THESE TERMS OR YOUR USE OF THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID TO
                FINDERACADEMY IN THE 12 MONTHS PRECEDING THE CLAIM.
              </Text>
            </LegalSection>

            <LegalSection title="9. Governing Law">
              <Text c="var(--sat-text-muted)" lh={1.8}>
                These Terms are governed by the laws of the State of California, United States,
                without regard to its conflict-of-law provisions. Any dispute arising from these Terms
                shall be resolved exclusively in the state or federal courts located in San Francisco
                County, California.
              </Text>
            </LegalSection>

            <LegalSection title="10. Changes to Terms">
              <Text c="var(--sat-text-muted)" lh={1.8}>
                We reserve the right to modify these Terms at any time. We will provide at least 14
                days' notice of material changes via email or in-app notification. Your continued use
                of the Service after the effective date of revised Terms constitutes your acceptance.
              </Text>
            </LegalSection>

            <LegalSection title="11. Termination">
              <Text c="var(--sat-text-muted)" lh={1.8}>
                FinderAcademy may suspend or terminate your account at any time for violation of these
                Terms. You may close your account at any time by contacting support@finderacademy.org.
                Sections 5, 7, 8, and 9 survive termination.
              </Text>
            </LegalSection>

            <LegalSection title="12. Contact">
              <Text c="var(--sat-text-muted)" lh={1.8}>
                FinderAcademy, Inc.<br />
                Email: support@finderacademy.org<br />
                Website: finderacademy.org
              </Text>
            </LegalSection>
          </Stack>
        </Box>
      </Container>
    </Box>
  )
}
